import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import { notification } from '@surecompanies/core_components'
import client from 'enrollment-platform/api/eps.client'
import { Person } from 'types/person'
import { extractApiErrorMessage } from 'helpers/api-error-message'
import { EnrollmentConfiguration } from './useEnrollmentConfiguration'

export type RescheduleEnrollmentDatesDto = {
  newEnrollmentStartDate?: string
  newEnrollmentEndDate?: string
  applyToAllEnrolled: boolean
  personIds?: string[]
}

export type RescheduleEnrollmentDatesResult = {
  successIds: string[]
  errors: string[]
}

/**
 * PUT .../enrollment/:enrollmentId/reschedule — the "Edit Dates" flows here
 * (company-wide and subset edits of an *existing* enrollment window).
 * useEnrollmentOutstanding's "Create Outstanding Enrollments" used to share
 * this route too, but was moved back onto its own dedicated endpoint — see
 * EMS-10168 — since this route's validation (active-window/pause guard,
 * overlap checks) is written for changing an existing window, not creating
 * a first one.
 */
export async function rescheduleEnrollmentDates(
  clientPublicKey: string,
  enrollmentId: string,
  dto: RescheduleEnrollmentDatesDto
): Promise<RescheduleEnrollmentDatesResult> {
  try {
    const response = await client.put<RescheduleEnrollmentDatesResult>(
      `/customers/${clientPublicKey}/configuration/enrollment/${enrollmentId}/reschedule`,
      dto
    )
    return response.data
  } catch (error: unknown) {
    // Surface the backend's validation message (e.g. "pause it instead",
    // conflicting enrollment) rather than axios' generic status-code text.
    // extractApiErrorMessage also flattens this app's {field, errors}[]
    // validation-error shape into readable text instead of "[object Object]".
    throw new Error(
      extractApiErrorMessage(error, 'Failed to update enrollment dates')
    )
  }
}

/**
 * The reschedule endpoint can return 200 with a partial failure — some
 * employees updated, others logged in `errors` (e.g. a write conflict) —
 * so a caller must inspect the result rather than treat any 200 as a clean
 * success. Shared by both "Edit Dates" flows so they report it consistently.
 */
export function notifyRescheduleResult(
  result: RescheduleEnrollmentDatesResult
) {
  if (result.errors?.length) {
    notification.warning({
      message: 'Enrollment dates partially updated',
      description: `${result.successIds.length} updated successfully. ${result.errors.length} failed: ${result.errors.join(', ')}`,
    })
  } else {
    notification.success({
      message: 'Enrollment dates updated successfully',
    })
  }
}

/**
 * Patches the cached "Enrollment Period Dates" rows with the dates we just
 * asked the backend to save, instead of invalidating the query and letting
 * it refetch immediately.
 *
 * The GET behind that query (`/enrollment/:id/members`) is backed by a
 * DynamoDB GSI query (`ClientPublicKey-index`) — see
 * `enrollment-configuration.service.ts`'s `findEnrollmentsForOep`, the same
 * lookup a company-wide reschedule uses to resolve *which* employees to
 * write to. GSIs are always eventually consistent in DynamoDB (there is no
 * strongly-consistent read option for them), so a refetch that races a
 * bulk write of dozens of parallel `UpdateItem` calls can land mid-
 * propagation: some rows already reflect the new dates, others still show
 * the old ones — exactly the "many EEs still show the previous Start/End
 * Dates" symptom. Patching the rows we already know succeeded sidesteps
 * that window entirely; the query is still marked stale (not refetched) so
 * a later natural refetch (e.g. reopening this panel) re-syncs once the
 * index has caught up.
 */
function patchEnrollmentPeriodDatesCache(
  queryClient: QueryClient,
  {
    clientPublicKey,
    enrollmentId,
    dto,
    successIds,
  }: {
    clientPublicKey: string
    enrollmentId: string
    dto: RescheduleEnrollmentDatesDto
    successIds: string[]
  }
) {
  const queryKey = ['enrollmentPeriodDates', clientPublicKey, enrollmentId]
  const successIdSet = new Set(successIds)

  queryClient.setQueryData<{ members: Person[]; total: number }>(
    queryKey,
    (current) => {
      if (!current) return current
      return {
        ...current,
        members: current.members.map((member) =>
          member.employeePublicKey && successIdSet.has(member.employeePublicKey)
            ? {
                ...member,
                // `Person`'s date fields are typed as `Date` for convenience,
                // but the API actually returns plain ISO date strings (see
                // the doc comment on `Person` in types/person.ts) — every
                // other row in this cache is still that raw string. Patching
                // with `new Date(...)` here would (a) parse as UTC midnight,
                // a day early in any locally-rendered consumer, and (b)
                // leave the array with mixed Date/string values, which
                // breaks ag-grid's default quick-filter and sort on these
                // columns. Keep the same YYYY-MM-DD string shape instead.
                enrollmentStartDate: (dto.newEnrollmentStartDate ??
                  member.enrollmentStartDate) as Person['enrollmentStartDate'],
                enrollmentEndDate: (dto.newEnrollmentEndDate ??
                  member.enrollmentEndDate) as Person['enrollmentEndDate'],
              }
            : member
        ),
      }
    }
  )

  queryClient.invalidateQueries({ queryKey, refetchType: 'none' })
}

export function useEnrollmentPeriodDates(clientPublicKey: string) {
  const queryClient = useQueryClient()

  /* --------------------------------- Methods -------------------------------- */
  const fetchEnrollmentPeriodDates = async (enrollmentId: string) => {
    const response = await client.get<{
      members: Person[]
      total: number
    }>(
      `/customers/${clientPublicKey}/configuration/enrollment/${enrollmentId}/members`
    )
    return response.data
  }

  /* ---------------------------------- Query --------------------------------- */
  const queryEnrollmentPeriodDates = (
    enrollment: EnrollmentConfiguration,
    options?: {
      queryOptions?: UseQueryOptions<{
        members: Person[]
        total: number
      }>
      enabled?: boolean
    }
  ) => {
    const enrollmentId = enrollment.id?.toString()
    return useQuery({
      queryKey: ['enrollmentPeriodDates', clientPublicKey, enrollmentId],
      queryFn: () => fetchEnrollmentPeriodDates(enrollmentId as string),
      staleTime: 0,
      enabled:
        options?.enabled !== false && !!clientPublicKey && !!enrollmentId,
      refetchOnWindowFocus: false,
      ...options?.queryOptions,
    })
  }

  /* -------------------------------- Mutation --------------------------------- */
  const rescheduleEnrollmentMutation = (options?: {
    mutationOptions?: UseMutationOptions<
      RescheduleEnrollmentDatesResult,
      Error,
      { enrollmentId: string; dto: RescheduleEnrollmentDatesDto }
    >
  }) => {
    return useMutation<
      RescheduleEnrollmentDatesResult,
      Error,
      { enrollmentId: string; dto: RescheduleEnrollmentDatesDto }
    >({
      mutationFn: ({ enrollmentId, dto }) =>
        rescheduleEnrollmentDates(clientPublicKey, enrollmentId, dto),
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({
          queryKey: ['getEnrollmentConfiguration', clientPublicKey],
        })
        queryClient.invalidateQueries({
          queryKey: ['enrollmentOutstanding', clientPublicKey],
        })
        patchEnrollmentPeriodDatesCache(queryClient, {
          clientPublicKey,
          enrollmentId: variables.enrollmentId,
          dto: variables.dto,
          successIds: data.successIds,
        })
      },
      ...options?.mutationOptions,
    })
  }

  return {
    queryEnrollmentPeriodDates,
    rescheduleEnrollmentMutation,
  }
}
